import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { useMpRoom } from "../multiplayer/useMpRoom";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { newRoomCode } from "../multiplayer/types";
import { AvatarPreview } from "../components/AvatarEditor";
import { SEED_LESSONS } from "../data/seed-lessons";
import { isLessonUnlocked } from "../local/profile";
import { SINGLE_COURSE_SEED } from "../game/courseConfig";

/**
 * Battle lobby. Shape:
 *   /battle                    → entry (Create / Join)
 *   /battle?code=XXXX&host=1   → host picks a lesson, opens room
 *   /battle?code=XXXX          → joiner waits in lobby
 *
 * Lessons in the picker respect the player's unlock state — same rule as
 * single-player level select.
 */
export function RaceLobby() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { profile } = useLocalProfile();
  const code = params.get("code");
  const asHost = params.get("host") === "1";
  const [chosenLessonId, setChosenLessonId] = useState<string | null>(null);

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }

  const me = {
    id: getBattlePlayerId(profile.id, code, asHost ? "host" : "join"),
    displayName: profile.displayName,
    avatarConfig: profile.avatarConfig,
  };
  const lessons = useMemo(
    () => [...SEED_LESSONS].sort((a, b) => a.orderIndex - b.orderIndex),
    [],
  );

  const joinOpts = useMemo(() => {
    if (!code) return null;
    if (asHost && !chosenLessonId) return null;
    return {
      code,
      asHost,
      me,
      lessonId: chosenLessonId ?? undefined,
      seed: asHost ? SINGLE_COURSE_SEED : undefined,
    };
  }, [code, asHost, me.id, chosenLessonId]);

  const { client, state, error } = useMpRoom(joinOpts);

  // 1) No code yet → entry screen.
  if (!code) return <RaceEntry />;

  // 2) Host hasn't picked a lesson → lesson picker.
  if (asHost && !chosenLessonId) {
    return (
      <div className="shell-narrow stack" style={{ paddingTop: "4vh" }}>
        <button className="btn btn-ghost" onClick={() => nav("/battle")} style={{ alignSelf: "flex-start" }}>
          ← Back
        </button>
        <h1 className="t-title t-center">Pick a level</h1>
        <p className="t-body t-muted t-center">Everyone races on the same level.</p>
        <div className="grid-2">
          {lessons.map((l) => {
            const unlocked = isLessonUnlocked(profile, l.orderIndex, lessons);
            return (
              <button
                key={l.id}
                className="btn btn-blue"
                style={{ minHeight: 64, justifyContent: "flex-start", padding: "12px 16px" }}
                disabled={!unlocked}
                onClick={() => setChosenLessonId(l.id)}
              >
                <span style={{ marginRight: 8 }}>#{l.orderIndex}</span>
                {l.title.replace(/^Level \d+:\s*/, "")}
                {!unlocked && " 🔒"}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // 3) Connecting / failed.
  if (error) {
    return (
      <div className="shell-narrow stack" style={{ paddingTop: "8vh" }}>
        <h1 className="t-title t-center">Room not found</h1>
        <p className="t-body t-muted t-center">{error}</p>
        <button className="btn btn-primary btn-block" onClick={() => nav("/battle")}>
          Back
        </button>
      </div>
    );
  }
  if (!state) return <div className="full-center">Joining…</div>;

  // 4) Race started → bounce to play.
  if (state.phase === "racing" || state.phase === "countdown") {
    nav(`/battle/play?code=${state.code}`, { replace: true });
    return <div className="full-center">Loading race…</div>;
  }

  const meInRoom = state.players.find((p) => p.id === me.id);
  const allReady = state.players.length >= 2 && state.players.every((p) => p.ready);
  const isHost = state.hostId === me.id;
  const lesson = lessons.find((l) => l.id === state.lessonId);

  return (
    <div className="shell-narrow stack" style={{ paddingTop: "4vh" }}>
      <button className="btn btn-ghost" onClick={() => nav("/")} style={{ alignSelf: "flex-start" }}>
        ← Menu
      </button>
      <h1 className="t-title t-center">Battle lobby</h1>
      <div className="card stack">
        <div className="row-between">
          <span className="t-caption t-muted" style={{ textTransform: "none" }}>Room code</span>
          <span className="code-pill" style={{ fontSize: "1.4rem" }}>{state.code}</span>
        </div>
        <p className="t-body t-muted t-center" style={{ margin: 0 }}>
          Share this code with friends — they tap <strong>Battle</strong> and enter it.
        </p>
        {lesson && (
          <div className="row-between">
            <span className="t-caption t-muted" style={{ textTransform: "none" }}>Level</span>
            <strong>{lesson.title}</strong>
          </div>
        )}
        <div className="stack-tight">
          <span className="t-caption t-muted" style={{ textTransform: "none" }}>
            Players ({state.players.length})
          </span>
          {state.players.map((p) => (
            <div key={p.id} className="lobby-row">
              <div style={{ transform: "scale(0.5)", width: 40, height: 40 }}>
                <AvatarPreview cfg={p.avatarConfig} />
              </div>
              <strong>
                {p.displayName}
                {p.id === state.hostId && <span className="t-muted"> · host</span>}
                {p.id === me.id && <span className="t-muted"> · you</span>}
              </strong>
              <span className={`ready-pill ${p.ready ? "on" : ""}`}>
                {p.ready ? "READY" : "waiting"}
              </span>
            </div>
          ))}
        </div>
        <button
          className={meInRoom?.ready ? "btn btn-purple btn-block" : "btn btn-green btn-block"}
          onClick={() => client?.setReady(!meInRoom?.ready)}
        >
          {meInRoom?.ready ? "Cancel ready" : "I'm ready ✓"}
        </button>
        {isHost && (
          <button
            className="btn btn-primary btn-big btn-block"
            disabled={!allReady}
            onClick={() => client?.startRace()}
          >
            {state.players.length < 2
              ? "Need at least 2 players"
              : !allReady
                ? "Waiting for everyone…"
                : "Start race →"}
          </button>
        )}
      </div>
    </div>
  );
}

function RaceEntry() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const create = () => {
    const c = newRoomCode();
    nav(`/battle?code=${c}&host=1`);
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (c.length === 4) nav(`/battle?code=${c}`);
  };
  return (
    <div className="shell-narrow stack" style={{ paddingTop: "8vh" }}>
      <button className="btn btn-ghost" onClick={() => nav("/")} style={{ alignSelf: "flex-start" }}>
        ← Menu
      </button>
      <h1 className="t-title t-center">⚔ Battle</h1>
      <p className="t-body t-muted t-center" style={{ margin: 0 }}>
        Race a friend on the same level.
      </p>
      <div className="card stack">
        <button className="btn btn-orange btn-big btn-block" onClick={create}>
          🏁 Create room
        </button>
        <div className="row" style={{ alignItems: "center" }}>
          <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
          <span className="t-caption t-muted" style={{ padding: "0 12px" }}>or</span>
          <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.08)" }} />
        </div>
        <form onSubmit={submit} className="stack">
          <input
            className="input"
            placeholder="ABCD"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={4}
            style={{ textAlign: "center", letterSpacing: 8, fontSize: "1.8rem" }}
          />
          <button className="btn btn-blue btn-big btn-block" type="submit" disabled={code.trim().length < 4}>
            Join room
          </button>
        </form>
      </div>
      <p className="t-caption t-muted t-center" style={{ textTransform: "none" }}>
        Tip: open the site in two tabs to test it on this device.
      </p>
    </div>
  );
}
